import {UAParser} from 'my-ua-parser'

import {Device, DeviceType, Browser, BrowserApp, OS, OSType, MobileApp, MobileApps} from './constants'
import {
    ACCESS_AGENT,
    AccessAgent,
    APP_VERSION_AGENT_REGEX,
    APP_VERSION_NATIVE_REGEX,
    MOBILE_APP_TYPE,
    NAVER_APP_AGENT_REGEX,
    NAVER_APP_ANDROID_NATIVE_AGENT,
    NAVER_APP_IOS_NATIVE_AGENT,
    NAVER_APP_NATIVE_AGENT,
    NAVER_APP_WATCH_OS_NATIVE_AGENT,
    NAVER_APP_WEAR_OS_NATIVE_AGENT,
    PAY_APP_AGENT_REGEX,
    PAY_APP_ANDROID_NATIVE_AGENT,
    PAY_APP_IOS_NATIVE_AGENT,
    PAY_APP_NATIVE_AGENT,
    PAY_APP_WATCH_OS_NATIVE_AGENT,
    PAY_APP_WEAR_OS_NATIVE_AGENT,
    SHOPPING_APP_BROWSER_AGENT_REGEX,
    SHOPPING_APP_EXTRA_BROWSER_AGENT_REGEX,
    SHOPPING_APP_WEBVIEW_AGENT_REGEX,
} from './naverApp'

/**
 * UserAgentParser - 브라우저와 Node.js 환경에서 사용 가능한 User-Agent 파싱 클래스
 * Based on UAParser.js v1.0.41
 */
export class UserAgent {
    private ua = ''
    private browser: {
        name?: string
        version?: string
    } = {}

    private os: {
        name?: string
        version?: string
    } = {}

    private device: {
        /**
         * Determined dynamically
         */
        model?: string
        type?: string
        vendor?: string
    } = {}

    constructor(userAgent: string) {
        if (userAgent && typeof userAgent === 'string') {
            // 환경에 따른 User-Agent 문자열 설정
            this.ua = userAgent

            const {browser, os, device} = UAParser(userAgent)
            // 브라우저 정보 파싱
            this.browser = browser
            this.os = os
            this.device = device
        }
    }

    get userAgent(): string {
        return this.ua
    }

    get osName(): string {
        return this.os.name ?? ''
    }

    get isAndroid(): boolean {
        return this.os.name === OS.Android
    }

    get isChrome(): boolean {
        return this.browser.name === Browser.Chrome
    }

    get isDesktop(): boolean {
        return this.device.type === undefined
    }

    get isEdgeChromium() {
        return this.ua.indexOf('Edg/') !== -1
    }

    get isEdge(): boolean {
        return this.browser.name === Browser.Edge || this.isEdgeChromium
    }

    get isFirefox(): boolean {
        return this.browser.name === Browser.Firefox
    }

    get isIE(): boolean {
        const name = this.browser.name
        return name === Browser.InternetExplorer || name === Browser.IE
    }

    get isIOS(): boolean {
        return this.os.name === OS.IOS
    }

    get isMacOs(): boolean {
        return this.os.name === OS.MAC_OS
    }

    get isMobile(): boolean {
        const type = this.device.type
        return type === Device.Mobile || type === Device.Tablet
    }

    get isMobileOnly(): boolean {
        return this.device.type === Device.Mobile
    }

    get isPC(): boolean {
        return !this.isMobile
    }

    get isLinux(): boolean {
        return /Linux/.test(this.osName)
    }

    get isOpera(): boolean {
        return this.browser.name === Browser.Opera
    }

    get isSamsungBrowser(): boolean {
        return this.browser.name === Browser.SamsungBrowser
    }

    get isBrowser(): boolean {
        return this.device.type === Device.Browser
    }

    get isSafari(): boolean {
        const name = this.browser.name
        return name === Browser.Safari || name === Browser.MobileSafari
    }

    get isMobileSafari(): boolean {
        return this.browser.name === Browser.MobileSafari
    }

    get isWindows(): boolean {
        return this.os.name === OS.Windows
    }

    get isTablet(): boolean {
        return this.device.type === Device.Tablet
    }

    get isMobileDisplay(): boolean {
        return this.isMobile && !this.isDesktop
    }

    get mobileDisplayType(): 'true' | 'false' {
        return `${this.isMobileDisplay}`
    }

    get deviceType(): 'PC' | 'MOBILE' {
        return this.isMobile ? DeviceType.MOBILE : DeviceType.PC
    }

    get osType(): 'LINUX' | 'MAC_OS' | 'WINDOWS' | 'IOS' | 'ANDROID' | 'ETC' {
        if (this.isAndroid) {
            return OSType.ANDROID
        } else if (this.isIOS) {
            return OSType.IOS
        } else if (this.isWindows) {
            return OSType.WINDOWS
        } else if (this.isMacOs) {
            return OSType.MAC_OS
        } else if (this.isLinux) {
            return OSType.LINUX
        } else {
            return OSType.ETC
        }
    }

    get browserType(): string {
        if (this.isChrome) {
            return BrowserApp.CHROME
        } else if (this.isFirefox) {
            return BrowserApp.FIREFOX
        } else if (this.isSafari) {
            return BrowserApp.SAFARI
        } else if (this.isOpera) {
            return BrowserApp.OPERA
        } else if (this.isIE) {
            return BrowserApp.IE
        } else if (this.isEdge) {
            return BrowserApp.EDGE
        }
        return BrowserApp.ETC
    }

    get mobileAppType(): string {
        /**
         * @see https://developers.naver.com/docs/openmain/#5--[%EC%B0%B8%EA%B3%A0]-%EB%84%A4%EC%9D%B4%EB%B2%84%EC%95%B1-%EA%B5%AC%EB%B6%84-%EB%B0%A9%EB%B2%95
         * @description 네이버앱(search) / 페이앱(pay) UA 정규식, 네이버앱 UA 구분 방법은 위 링크를 참조하시면 됩니다.
         *              카페앱(cafe) https://wiki.navercorp.com/pages/viewpage.action?pageId=224341009
         *              네이버쇼핑앱(navershopping|navershoppingbrowser|shopping-sdk) https://wiki.navercorp.com/display/NEWSHOPNAPP/0.+UserAgent+Interface
         *  해당 키 업데이트시 MobileApps 수정 필수
         */
        const appUserAgentRegEx =
            /NAVER\([inapp|higgs]+;[^0-9]*(search|pay|cafe|navershopping|navershoppingbrowser|shopping-sdk|map);[^0-9]*(\d+);[^0-9]*(\d+.\d+.\d+).*\)/gim
        const appMatchingData = appUserAgentRegEx.exec(this.ua) ?? []
        const appName = appMatchingData?.[1]

        return (appName ? MobileApps[appName] : MobileApp.UNKNOWN) ?? MobileApp.UNKNOWN
    }

    get isCafeApp(): boolean {
        return this.mobileAppType === MobileApp.NAVER_CAFE
    }

    get isNaverApp(): boolean {
        return this.mobileAppType === MobileApp.NAVER
    }

    get isNaverShopping(): boolean {
        return this.mobileAppType === MobileApp.NAVER_SHOPPING
    }

    get isPayApp(): boolean {
        return this.mobileAppType === MobileApp.NAVER_PAY
    }

    get isNaverMapApp(): boolean {
        return this.mobileAppType === MobileApp.NAVER_MAP
    }

    get isNaverServiceApp(): boolean {
        return [this.isCafeApp, this.isNaverApp, this.isNaverShopping, this.isPayApp].some((v) => v)
    }

    get isNaverWatchOSNApp(): boolean {
        return NAVER_APP_WATCH_OS_NATIVE_AGENT.test(this.ua) || NAVER_APP_WEAR_OS_NATIVE_AGENT.test(this.ua)
    }

    get isWatchOSNApp(): boolean {
        return PAY_APP_WATCH_OS_NATIVE_AGENT.test(this.ua) || NAVER_APP_WATCH_OS_NATIVE_AGENT.test(this.ua)
    }

    get isWearOSNApp(): boolean {
        return PAY_APP_WEAR_OS_NATIVE_AGENT.test(this.ua) || NAVER_APP_WEAR_OS_NATIVE_AGENT.test(this.ua)
    }

    get isIOSNativeNApp(): boolean {
        return PAY_APP_IOS_NATIVE_AGENT.test(this.ua) || NAVER_APP_IOS_NATIVE_AGENT.test(this.ua)
    }

    get isAndroidNativeNApp(): boolean {
        return PAY_APP_ANDROID_NATIVE_AGENT.test(this.ua) || NAVER_APP_ANDROID_NATIVE_AGENT.test(this.ua)
    }

    get appVersion(): string | undefined {
        const nativeAppVersionData = APP_VERSION_NATIVE_REGEX.exec(this.ua)
        const appVersionData = APP_VERSION_AGENT_REGEX.exec(this.ua)
        return nativeAppVersionData ? nativeAppVersionData[1] : appVersionData ? appVersionData[1] : '0'
    }

    get InAppBrowserAppVersion(): string | undefined {
        const appVersionData = PAY_APP_AGENT_REGEX.exec(this.ua)
        return appVersionData?.[2]
    }

    get isNApp(): boolean {
        return (
            PAY_APP_AGENT_REGEX.test(this.ua) ||
            PAY_APP_NATIVE_AGENT.test(this.ua) ||
            NAVER_APP_AGENT_REGEX.test(this.ua) ||
            NAVER_APP_NATIVE_AGENT.test(this.ua)
        )
    }

    /** userAgent 문자열을 기반으로 access agent 를 반환합니다. */
    get accessAgent(): AccessAgent {
        return PAY_APP_AGENT_REGEX.test(this.ua) || PAY_APP_NATIVE_AGENT.test(this.ua)
            ? ACCESS_AGENT.MOBILE_PAY_APP
            : NAVER_APP_AGENT_REGEX.test(this.ua) || NAVER_APP_NATIVE_AGENT.test(this.ua)
              ? ACCESS_AGENT.MOBILE_APP
              : SHOPPING_APP_WEBVIEW_AGENT_REGEX.test(this.ua) ||
                  SHOPPING_APP_BROWSER_AGENT_REGEX.test(this.ua) ||
                  SHOPPING_APP_EXTRA_BROWSER_AGENT_REGEX.test(this.ua)
                ? ACCESS_AGENT.NAVER_SHOPPING_APP
                : ACCESS_AGENT.MOBILE_WEB
    }

    /**
     * 네이버 앱, 페이 앱, 쇼핑앱 접속 정보 반환
     * - this bind 조심
     **/
    getAccessInfo(
        accessAgent: AccessAgent,
        cookies: {
            get(name: string): string | undefined
        },
        nidGsKey = '',
    ): {
        accessAgent: AccessAgent
        accessKey?: string
        /** 네이버 앱 여부 */
        isNaverApp: boolean
        /** 네이버 쇼핑앱 여부 */
        isNaverShoppingApp: boolean
        /** 네이버 페이앱 여부 */
        isPayApp: boolean
        /** accessAgent 기반 모바일 앱 분류 */
        mobileAppType?: 'NAVER' | 'NAVER_PAY' | 'NAVER_SHOPPING'
    } {
        const isNaverApp = accessAgent === ACCESS_AGENT.MOBILE_APP
        const isNaverShoppingApp = accessAgent === ACCESS_AGENT.NAVER_SHOPPING_APP
        const isPayApp = accessAgent === ACCESS_AGENT.MOBILE_PAY_APP
        const isShoppingApp = accessAgent === ACCESS_AGENT.NAVER_SHOPPING_APP

        const currentDeviceID = cookies.get('NAPP_DI')
        const accessKey = isPayApp || isNaverApp || isShoppingApp ? currentDeviceID : nidGsKey

        return {
            accessAgent,
            accessKey,
            isNaverApp,
            isNaverShoppingApp,
            isPayApp,
            mobileAppType: MOBILE_APP_TYPE[accessAgent],
        }
    }
}
